import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import { useEffect, useMemo, useState } from 'react';
import {
  BaseTransaction,
  Filters,
  IconLoader,
  Table,
  useFilters,
  useQueryState,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { OrganizationType } from '../types';
import { StatementsContextType } from './types';
import { overviewAccountsSchema } from './variables/statements-accounts';
import formatAmlTransacationList from './helpers/format-aml-transactions-list';
import { clearCardsTransactionState } from '../store/slices/statements';
import {
  amlAccountsColumns,
  amlAccountsColumnsLabels,
  amlAccountsFiltersSchema,
} from './variables/statements-aml';

import cn from './styles.module.css';

const AmlTransactionStatements = ({
  customerType,
}: {
  customerType: OrganizationType;
}) => {
  const [accountId, setAccountId] = useState('');

  const {
    accounts,
    customerTransactionsIsFetching,
    customerTransactionsIsLoading,
    errorList,
    sendMessage,
    setPage,
  } = useOutletContext<
    StatementsContextType & {
      customerTransactionsIsFetching?: boolean;
      customerTransactionsIsLoading?: boolean;

      errorList?: string;
      sendMessage: SendMessage;
      setPage?: ({
        id,
        take,
        filters,
      }: {
        id: string;
        take: string;
        filters: Filters;
      }) => void;
    }
  >();

  const dispatch = useAppDispatch();
  const { customerId } = useParams();

  const { amlTransactions, pagination, amlTransactionsErrorsList } =
    useAppSelector((state) => state.customerAml);
  const { customersTransactionTypes } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const { currencies: currenciesList } = useAppSelector((state) => state.root);

  const { filters, sort } = useQueryState(overviewAccountsSchema);

  const { filtersComponent, handleSubmit, reset } = useFilters({
    filtersSchema: amlAccountsFiltersSchema,
    filters,
    onFilterChange: () => {},
  });

  const formattedData = useMemo(() => {
    const data = formatAmlTransacationList(
      amlTransactions as BaseTransaction[],
      customerId,
      customerType,
      customersTransactionTypes,
      currenciesList,
    );

    return data;
  }, [amlTransactions, customerType]);

  useEffect(() => {
    reset(filters);
  }, [filters, reset]);

  useEffect(() => {
    if (!accountId && accounts) {
      setAccountId(accounts[0]?.id);
    }
  }, [accountId, accounts]);

  useEffect(() => {
    dispatch(clearCardsTransactionState());
  }, [dispatch]);

  let content;

  if (customerTransactionsIsLoading) {
    content = (
      <div className={cn.overviewStatementsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={amlAccountsColumns}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        sort={sort}
        setPage={(page) => setPage && setPage({ ...page, filters })}
        take="10"
        pagination={pagination}
        columnsLabels={amlAccountsColumnsLabels}
        onFilterChange={() => {}}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={customerTransactionsIsFetching}
        tableFormClassName={cn.overviewStatementsTableForm}
        tableHeadCellClassName={cn.overviewStatementsTableHeadCell}
        error={amlTransactionsErrorsList || errorList}
        transactionTable
        clickable
      />
    );
  }

  return (
    <>
      <div
        className={`${cn.overviewStatementsContentTable} ${
          customerTransactionsIsFetching ? cn.tableLoading : ''
        }`}
      >
        {content}
      </div>
      <Outlet context={{ sendMessage }} />
    </>
  );
};

export default AmlTransactionStatements;
